import { Helmet } from 'react-helmet-async';
import { useRecoilValue } from 'recoil';
import { useEffect, useRef } from 'react';
import { Product } from '../../interfaces/Product/Product';
import { ProductReview } from '../../interfaces/Product/ProductReview';
import { settingsAtom } from '../../atoms/Settings';

type ProductHelmetProps = {
  product: Product;
  review?: ProductReview;
};

export const ProductHelmet = ({ product, review }: ProductHelmetProps) => {
  const FE_URL = process.env.REACT_APP_FE_URL;

  const settings = useRecoilValue(settingsAtom);

  const defaultTitle = `${product.meta.title || product.name} | ${
    settings.settingsV1.storefront_navbar_text || 'NNET E-Ticaret'
  }`;

  const hiddenTitle = settings.settingsV1.storefront_hidden_title;

  const titleRef = useRef(defaultTitle);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && hiddenTitle) {
        document.title = hiddenTitle;
      } else {
        document.title = titleRef.current;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <Helmet>
      <title>{defaultTitle}</title>
      <meta
        name='description'
        content={product.meta.description || product.shortDescription}
      />
      <meta property='og:title' content={product.meta.title || product.name} />
      <meta
        property='og:description'
        content={product.meta.description || product.shortDescription}
      />
      <meta property='og:image' content={product.baseImage?.path} />
      <link
        rel='canonical'
        href={
          product.meta.canonicalURL
            ? `${FE_URL}/${product.meta.canonicalURL}`
            : `${FE_URL}/urun/${product.slug}`
        }
      />

      <script type='application/ld+json'>
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          image: [product.baseImage?.path, ...product.files.map((f) => f.path)],
          description: product.shortDescription,
          sku: product.sku,
          brand: {
            '@type': 'Brand',
            name: product.brand.name,
          },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'TRY',
            price: (
              product.specialPrice?.amount ?? product.price.amount
            ).toFixed(2),
            availability:
              product.qty > 0
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
          },
          ...(review?.totalCount
            ? {
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: (review.avgRating ?? 0).toFixed(1),
                  reviewCount: review.totalCount,
                },
              }
            : {}),
        })}
      </script>

      <script type='application/ld+json'>
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Ana Sayfa',
              item: `${FE_URL}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: product.brand.name,
              item: `${FE_URL}/marka/${product.brand.slug}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: product.name,
              item: `${FE_URL}/urun/${product.slug}`,
            },
          ],
        })}
      </script>
    </Helmet>
  );
};
